import hiringManager from '@/assets/boss.png';
import logo from '@/assets/hiredcoach-logo.svg';
import coachalina from '@/assets/coachalina.svg';
import coachelle from '@/assets/coachelle.svg';
import gecko from '@/assets/gecko.svg';
import UserAvatar from '@/components/avatar/UserAvatar';
import { DashboardLayout } from '@/components/layouts/DashboardLayout';
import PremiumLockModal from '@/components/subscription/PremiumLockModal';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import type { PersonalityInfo } from '@/lib/api';
import { JobApiResponse, SalaryNegotiationResponse, useApiClient } from '@/lib/api';
import { getUserErrorMessage } from '@/lib/error-messages';
import { cn } from '@/lib/utils';
import { ArrowLeft, CheckCircle, ChevronDown, ChevronUp, Clock, Lightbulb, MessageCircle, Mic, Pause, Play, RotateCcw, StopCircle, Target, TrendingUp } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

interface RouteParams {
  negotiationId?: string;
  jobId?: string;
}

// Audio Player Component for hiring manager messages
const AudioPlayerButton: React.FC<{ src: string; className?: string }> = ({ src, className }) => {
  const audioRef = React.useRef<HTMLAudioElement>(null);
  const [status, setStatus] = React.useState<'idle' | 'playing' | 'paused' | 'ended'>('idle');

  React.useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const handleEnded = () => setStatus('ended');
    audio.addEventListener('ended', handleEnded);
    return () => {
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  const handlePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.play().catch((err) => {
      console.error('AudioPlayerButton: play() failed', err);
    });
    setStatus('playing');
  };

  const handlePause = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    setStatus('paused');
  };

  const handleReset = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = 0;
    audio.play().catch((err) => {
      console.error('AudioPlayerButton: play() failed', err);
    });
    setStatus('playing');
  };

  // Sync state if user interacts with native controls (fallback)
  const handleAudioPlay = () => setStatus('playing');
  const handleAudioPause = () => setStatus('paused');

  // Don't render if there's no src
  if (!src) {
    return null;
  }

  return (
    <div className={className || "flex items-center justify-center"}>
      <audio
        ref={audioRef}
        src={src}
        style={{ display: 'none' }}
        onPlay={handleAudioPlay}
        onPause={handleAudioPause}
      />
      {status === 'idle' || status === 'paused' ? (
        <button
          className="w-12 h-12 rounded-full bg-primary text-white hover:bg-primary/90 transition flex items-center justify-center shadow focus:outline-none"
          onClick={handlePlay}
          aria-label="Play audio"
        >
          <Play className="w-5 h-5" />
        </button>
      ) : status === 'playing' ? (
        <button
          className="w-12 h-12 rounded-full bg-secondary text-primary hover:bg-secondary/80 transition flex items-center justify-center shadow focus:outline-none"
          onClick={handlePause}
          aria-label="Pause audio"
        >
          <Pause className="w-5 h-5" />
        </button>
      ) : (
        <button
          className="w-12 h-12 rounded-full bg-primary text-white hover:bg-primary/90 transition flex items-center justify-center shadow focus:outline-none"
          onClick={handleReset}
          aria-label="Restart audio"
        >
          <RotateCcw className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

export default function SalaryNegotiationPage() {
  const { api } = useApiClient();
  const navigate = useNavigate();
  const { negotiationId, jobId } = useParams<RouteParams>();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isPremium = (user?.subscription_mode ?? 'free') === 'premium';

  const [negotiation, setNegotiation] = useState<SalaryNegotiationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [input, setInput] = useState('');
  const [expandedMessages, setExpandedMessages] = useState<Set<number>>(new Set());
  
  // Speech-to-text state
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  
  // Premium lock modal state
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [rateLimitInfo, setRateLimitInfo] = useState<{
    used: number;
    limit: number;
    windowEnd: string | null;
  } | null>(null);
  
  // Ref for auto-scrolling
  const conversationEndRef = useRef<HTMLDivElement>(null);

  // Setup form state
  const [showSetup, setShowSetup] = useState(false);
  const [job, setJob] = useState<JobApiResponse | null>(null);
  const [listedSalary, setListedSalary] = useState('');
  const [desiredSalary, setDesiredSalary] = useState('');
  const [personality, setPersonality] = useState<'friendly' | 'corporate_neutral' | 'aggressive' | 'data_driven'>('corporate_neutral');
  const [customScenarioDescription, setCustomScenarioDescription] = useState('');
  const [personalities, setPersonalities] = useState<PersonalityInfo[]>([]);
  const [personalityImageMap, setPersonalityImageMap] = useState<Record<string, string>>({});

  const scenarioType = searchParams.get('scenario') || 'employer_asks_your_range';

  // Fetch job details if jobId is provided
  useEffect(() => {
    const fetchJob = async () => {
      if (jobId) {
        try {
          const jobData = await api.getJobById(jobId);
          setJob(jobData);
          // Pre-fill listed salary from job if available
          if (jobData.salary) {
            setListedSalary(jobData.salary);
          }
        } catch (e) {
          console.error('Failed to fetch job:', e);
        }
      }
    };
    fetchJob();
  }, [jobId, api]);

  // Fetch personalities
  useEffect(() => {
    const fetchPersonalities = async () => {
      try {
        const data = await api.getPersonalities();
        // Sort in specific order: corporate_neutral, friendly, data_driven, aggressive
        const order = ['corporate_neutral', 'friendly', 'data_driven', 'aggressive'];
        const sortedData = [...data].sort((a, b) => {
          const aIndex = order.indexOf(a.id);
          const bIndex = order.indexOf(b.id);
          // If not found in order, put at end
          if (aIndex === -1) return 1;
          if (bIndex === -1) return -1;
          return aIndex - bIndex;
        });
        setPersonalities(sortedData);
        // Map personality IDs to imported images for reliable asset loading
        const personalityImageImports: Record<string, string> = {
          'corporate_neutral': hiringManager,
          'friendly': coachelle,
          'data_driven': coachalina,
          'aggressive': gecko,
        };
        const imageMap: Record<string, string> = {};
        sortedData.forEach(p => {
          // Use imported image if available, otherwise fall back to API URL
          imageMap[p.id] = personalityImageImports[p.id] || p.image_url;
        });
        setPersonalityImageMap(imageMap);
      } catch (e) {
        console.error('Failed to fetch personalities:', e);
      }
    };
    fetchPersonalities();
  }, [api]);

  useEffect(() => {
    const init = async () => {
      try {
        if (negotiationId) {
          const existing = await api.getNegotiationById(negotiationId);
          setNegotiation(existing);
          setLoading(false);
        } else {
          // Show setup form for new negotiations
          setShowSetup(true);
          setLoading(false);
        }
      } catch (e: any) {
        console.error(e);
        toast.error(
          getUserErrorMessage(
            e,
            'Unable to load negotiation right now. Please try again later.'
          )
        );
        setLoading(false);
      }
    };
    init();
  }, [negotiationId, api]);

  const handleSetupSubmit = async () => {
    if (!listedSalary.trim() && !desiredSalary.trim()) {
      toast.error('Please provide at least a listed salary or desired salary');
      return;
    }

    // Validate custom scenario description
    if (scenarioType === 'custom') {
      const words = customScenarioDescription.trim().split(/\s+/).filter(w => w.length > 0);
      if (words.length < 3) {
        toast.error('Please provide at least 3 words describing your custom scenario');
        return;
      }
    }

    setLoading(true);
    try {
      // Minimal scenario config prototype
      const config: Record<string, any> = {};
      if (scenarioType === 'far_apart_on_salary') {
        config.candidate_target = searchParams.get('target') || '';
        config.employer_offer = searchParams.get('offer') || '';
      } else if (scenarioType === 'employer_fixed_budget') {
        config.budget_cap = searchParams.get('cap') || '';
      } else if (scenarioType === 'competing_offer') {
        config.competing_offer_strength = searchParams.get('strength') || 'medium';
      } else if (scenarioType === 'custom') {
        config.custom_description = customScenarioDescription.trim();
      }

      const created = await api.createNegotiation({
        job_id: jobId,
        scenario_type: scenarioType,
        scenario_config: config,
        listed_salary: listedSalary.trim() || undefined,
        desired_salary: desiredSalary.trim() || undefined,
        personality: personality,
      });
      setNegotiation(created);
      setShowSetup(false);
      navigate(`/jobs/${jobId}/negotiation/${created.id}`, { replace: true });
    } catch (e: any) {
      console.error(e);
      // Handle subscription/rate limit errors
      if (e.statusCode === 405) {
        const errorDetail = e.detail || {};
        if (errorDetail.window === 'week' && errorDetail.used >= errorDetail.limit) {
          // Rate limit reached - store info and show modal
          setRateLimitInfo({
            used: errorDetail.used || 0,
            limit: errorDetail.limit || 1,
            windowEnd: errorDetail.window_end || null,
          });
          setShowPremiumModal(true);
        } else {
          // Other access denied - show premium modal
          setRateLimitInfo(null);
          setShowPremiumModal(true);
        }
      } else {
        toast.error(
          getUserErrorMessage(
            e,
            'Unable to create negotiation right now. Please try again later.'
          )
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!negotiation || negotiation.status !== 'GRADING') return;
    let cancelled = false;
    const interval = setInterval(async () => {
      try {
        if (!negotiation.id) return;
        const data = await api.getNegotiationById(negotiation.id);
        if (!cancelled) {
          setNegotiation(data);
          if (data.status !== 'GRADING') {
            clearInterval(interval);
          }
        }
      } catch {
        // ignore polling errors
      }
    }, 5000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [negotiation, api]);


  const handleSend = async () => {
    if (!negotiation || !input.trim()) return;
    
    const userMessage = input.trim();
    setInput('');
    setSending(true);
    
    // Optimistic update: show user's message immediately
    const optimisticMessage = {
      role: 'user' as const,
      content: userMessage,
      created_at: new Date().toISOString(),
    };
    
    setNegotiation(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        messages: [...prev.messages, optimisticMessage],
      };
    });
    
    try {
      const updated = await api.sendNegotiationMessage(negotiation.id, userMessage);
      setNegotiation(updated);
      // Auto-scroll will happen via useEffect when messages change
    } catch (e: any) {
      console.error(e);
      toast.error(
        getUserErrorMessage(
          e,
          'Unable to send your message right now. Please try again later.'
        )
      );
      // Revert optimistic update on error - remove the last user message
      setNegotiation(prev => {
        if (!prev) return prev;
        const newMessages = [...prev.messages];
        // Find and remove the optimistic message we just added
        const lastIndex = newMessages.length - 1;
        if (lastIndex >= 0 && 
            newMessages[lastIndex].role === 'user' && 
            newMessages[lastIndex].content === userMessage) {
          newMessages.pop();
        }
        return {
          ...prev,
          messages: newMessages,
        };
      });
      // Restore input text so user can retry
      setInput(userMessage);
    } finally {
      setSending(false);
    }
  };


  const handleFinish = async () => {
    if (!negotiation) return;
    setFinishing(true);
    try {
      const updated = await api.finishNegotiation(negotiation.id);
      setNegotiation(updated);
      toast.success('Negotiation submitted for grading');
    } catch (e: any) {
      console.error(e);
      toast.error(
        getUserErrorMessage(
          e,
          'Unable to finish negotiation right now. Please try again later.'
        )
      );
    } finally {
      setFinishing(false);
    }
  };

  // Auto-scroll to bottom when messages change (only during active conversation)
  useEffect(() => {
    if (!negotiation) return;
    const isComplete = negotiation.status === 'COMPLETE';
    const isGrading = negotiation.status === 'GRADING';
    if (conversationEndRef.current && !isComplete && !isGrading) {
      conversationEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [negotiation?.messages, negotiation?.status]);

  // Handle terminated state - show termination message, then transition to grading after delay
  useEffect(() => {
    if (!negotiation) return;
    const isTerminatedStatus = negotiation.status === 'TERMINATED';
    if (isTerminatedStatus && negotiation.outcome) {
      // Show notification about termination
      const outcomeMessages: Record<string, string> = {
        'AGREEMENT_REACHED': 'The hiring manager has accepted your terms! The negotiation is complete.',
        'EMPLOYER_HELD_FIRM': 'The hiring manager has made their final offer and is not willing to negotiate further.',
        'USER_WITHDREW': 'You have withdrawn from the negotiation.',
        'STALEMATE': 'The negotiation has reached a stalemate.',
      };
      const message = outcomeMessages[negotiation.outcome] || 'The negotiation has ended.';
      toast.info(message, { duration: 20000 });

      // Transition to grading after 20 seconds
      const timer = setTimeout(() => {
        if (negotiation.id) {
          api.finishNegotiation(negotiation.id).then(setNegotiation).catch((e) => {
            console.error('Failed to transition to grading:', e);
            toast.error('Failed to start grading. Please try refreshing.');
          });
        }
      }, 20000);

      return () => clearTimeout(timer);
    }
  }, [negotiation?.status, negotiation?.outcome, negotiation?.id, api]);

  // Speech-to-text helpers
  const isMicrophoneSupported = () => {
    return typeof navigator !== 'undefined' && 
           'mediaDevices' in navigator && 
           'getUserMedia' in navigator.mediaDevices;
  };

  const getSupportedAudioFormat = () => {
    const formats = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/ogg;codecs=opus',
      'audio/wav'
    ];
    
    for (const format of formats) {
      if (MediaRecorder.isTypeSupported(format)) {
        return format;
      }
    }
    return 'audio/webm'; // fallback
  };

  const handleMicClick = async () => {
    if (!isMicrophoneSupported()) {
      toast.error('Microphone not supported on this device');
      return;
    }

    if (isRecording) {
      // Stop recording
      if (mediaRecorder && mediaRecorder.state === 'recording') {
        mediaRecorder.stop();
      }
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100
        }
      });

      const format = getSupportedAudioFormat();
      const recorder = new MediaRecorder(stream, { mimeType: format });
      const chunks: Blob[] = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      recorder.onstop = async () => {
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        
        setIsRecording(false);
        setRecordingTime(0);

        if (chunks.length === 0) {
          toast.error('No audio data recorded');
          return;
        }

        try {
          setIsTranscribing(true);
          const audioBlob = new Blob(chunks, { type: format });
          
          if (audioBlob.size === 0) {
            toast.error('Empty audio recording');
            return;
          }

          const formData = new FormData();
          const extension = format.includes('webm') ? '.webm' : 
                         format.includes('mp4') ? '.mp4' : 
                         format.includes('ogg') ? '.ogg' : '.wav';
          formData.append('file', audioBlob, `recording${extension}`);

          const transcription = await api.transcribeNegotiationAudio(formData);
          
          if (transcription && transcription.trim()) {
            let cleaned = transcription.trim();
            if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
              cleaned = cleaned.slice(1, -1).trim();
            }
            setInput(prev => prev ? `${prev} ${cleaned}` : cleaned);
          } else {
            toast.error('No speech detected in recording');
          }
        } catch (error) {
          console.error('Transcription error:', error);
          toast.error('Failed to transcribe audio. Please try again.');
        } finally {
          setIsTranscribing(false);
        }
      };

      recorder.onerror = (event: any) => {
        console.error('MediaRecorder error:', event);
        toast.error('Recording error occurred');
        setIsRecording(false);
        setRecordingTime(0);
      };

      // Update recording time every second
      let timer: NodeJS.Timeout | null = null;
      const originalOnStop = recorder.onstop;
      recorder.onstop = async () => {
        if (timer) clearInterval(timer);
        if (originalOnStop) originalOnStop();
        
        stream.getTracks().forEach(track => track.stop());
        setIsRecording(false);
        setRecordingTime(0);

        if (chunks.length === 0) {
          toast.error('No audio data recorded');
          return;
        }

        try {
          setIsTranscribing(true);
          const audioBlob = new Blob(chunks, { type: format });
          
          if (audioBlob.size === 0) {
            toast.error('Empty audio recording');
            return;
          }

          const formData = new FormData();
          const extension = format.includes('webm') ? '.webm' : 
                         format.includes('mp4') ? '.mp4' : 
                         format.includes('ogg') ? '.ogg' : '.wav';
          formData.append('file', audioBlob, `recording${extension}`);

          const transcription = await api.transcribeNegotiationAudio(formData);
          
          if (transcription && transcription.trim()) {
            let cleaned = transcription.trim();
            if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
              cleaned = cleaned.slice(1, -1).trim();
            }
            setInput(prev => prev ? `${prev} ${cleaned}` : cleaned);
          } else {
            toast.error('No speech detected in recording');
          }
        } catch (error) {
          console.error('Transcription error:', error);
          toast.error('Failed to transcribe audio. Please try again.');
        } finally {
          setIsTranscribing(false);
        }
      };

      setMediaRecorder(recorder);
      recorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Update recording time every second
      timer = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (error: any) {
      console.error('Microphone access error:', error);
      
      let errorMessage = 'Failed to access microphone';
      if (error.name === 'NotAllowedError') {
        errorMessage = 'Microphone access denied. Please allow microphone access and try again.';
      } else if (error.name === 'NotFoundError') {
        errorMessage = 'No microphone found. Please connect a microphone and try again.';
      } else if (error.name === 'NotSupportedError') {
        errorMessage = 'Microphone not supported on this device.';
      } else if (error.name === 'NotReadableError') {
        errorMessage = 'Microphone is being used by another application.';
      }
      
      toast.error(errorMessage);
    }
  };

  // Score letter calculation helper
  function getScoreLetter(score: number | null | undefined) {
    if (score == null) return '-';
    else if (score > 3.9) return "S"
    else if (score > 3.4) return "A"
    else if (score > 2.9) return "B"
    else if (score > 1.9) return "C"
    else if (score > 1.4) return "D"
    else return "F"
  }

  // Grade background for overall badge
  function getOverallGradeBg(score: number | null | undefined) {
    const letter = getScoreLetter(score);
    switch (letter) {
      case 'S': return 'bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 text-amber-900 border border-amber-300 shadow-lg shadow-amber-300/30';
      case 'A': return 'bg-green-300 text-green-900';
      case 'B': return 'bg-green-200 text-green-700';
      case 'C': return 'bg-yellow-200 text-yellow-700';
      case 'D': return 'bg-red-200 text-red-700';
      case 'F': return 'bg-red-500 text-red-900';
      default: return 'bg-gray-100 text-gray-800';
    }
  }

  // Header background color based on grade
  function getHeaderGradeBg(score: number | null | undefined) {
    const letter = getScoreLetter(score);
    switch (letter) {
      case 'S': return 'bg-gradient-to-br from-amber-100 via-yellow-100 to-amber-200';
      case 'A': return 'bg-gradient-to-br from-green-100 to-green-200';
      case 'B': return 'bg-gradient-to-br from-green-50 to-green-100';
      case 'C': return 'bg-gradient-to-br from-yellow-100 to-yellow-200';
      case 'D': return 'bg-gradient-to-br from-red-100 to-red-200';
      case 'F': return 'bg-gradient-to-br from-red-200 to-red-300';
      default: return 'bg-gradient-to-br from-blue-50 to-cyan-50';
    }
  }

  // Grade text color for header
  function getGradeTextColor(score: number | null | undefined) {
    const letter = getScoreLetter(score);
    switch (letter) {
      case 'S': return 'text-amber-800';
      case 'A': return 'text-green-800';
      case 'B': return 'text-green-700';
      case 'C': return 'text-yellow-800';
      case 'D': return 'text-red-700';
      case 'F': return 'text-red-800';
      default: return 'text-gray-800';
    }
  }

  // Score badge color helper
  function getScoreBadgeClass(letter: string) {
    switch (letter) {
      case 'S': return 'bg-gradient-to-r from-teal-400 to-blue-500 text-white';
      case 'A': return 'bg-blue-100 text-blue-800';
      case 'B': return 'bg-green-100 text-green-800';
      case 'C': return 'bg-yellow-100 text-yellow-800';
      case 'D': return 'bg-orange-100 text-orange-800';
      case 'F': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getScenarioDisplayName = (scenarioType: string) => {
    const names: Record<string, string> = {
      'employer_asks_your_range': 'They Ask for Your Range First',
      'far_apart_on_salary': 'Far Apart on Salary',
      'employer_fixed_budget': 'Employer Has Fixed Budget',
      'competing_offer': 'Competing Offer',
    };
    if (names[scenarioType]) {
      return names[scenarioType];
    }
    // Fallback: convert snake_case to Title Case
    return scenarioType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-8 text-center">Loading negotiation...</div>
      </DashboardLayout>
    );
  }

  // Show setup form for new negotiations
  if (showSetup) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/salary-negotiations')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
          </div>

          <div>
            <h1 className="text-2xl font-bold mb-2">Setup Salary Negotiation</h1>
            <p className="text-gray-600">
              Configure the negotiation parameters before starting the practice session.
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Salary Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="listed-salary">Listed Salary (from job posting)</Label>
                <Input
                  id="listed-salary"
                  placeholder={job?.salary || "e.g., $120,000 - $150,000"}
                  value={listedSalary}
                  onChange={(e) => setListedSalary(e.target.value)}
                />
                {job?.salary && (
                  <p className="text-xs text-gray-500 mt-1">
                    Pre-filled from job posting. You can edit this.
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="desired-salary">Your Desired Salary</Label>
                <Input
                  id="desired-salary"
                  placeholder="e.g., $140,000"
                  value={desiredSalary}
                  onChange={(e) => setDesiredSalary(e.target.value)}
                />
                <p className="text-xs text-gray-500 mt-1">
                  The salary you're targeting in this negotiation.
                </p>
              </div>
            </CardContent>
          </Card>

          {scenarioType === 'custom' && (
            <Card>
              <CardHeader>
                <CardTitle>Custom Scenario Description</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="custom-scenario">Describe your custom negotiation scenario</Label>
                  <Textarea
                    id="custom-scenario"
                    placeholder="e.g., The company is offering equity instead of higher salary, or The role has unusual hours that need compensation adjustment..."
                    value={customScenarioDescription}
                    onChange={(e) => setCustomScenarioDescription(e.target.value)}
                    rows={4}
                    className="mt-2"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Please provide at least 3 words describing the scenario. This will guide the hiring manager's approach.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Negotiator Personality</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-4">
                {personalities.length > 0 ? (
                  personalities.map((p) => (
                    <div
                      key={p.id}
                      onClick={() => setPersonality(p.id as any)}
                      className={cn(
                        "flex items-center gap-3 sm:gap-6 p-3 sm:p-4 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md",
                        personality === p.id
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 bg-white hover:border-gray-300"
                      )}
                      style={{ minWidth: 0 }}
                    >
                      <div className="flex-shrink-0">
                        <img
                          src={personalityImageMap[p.id] || p.image_url || hiringManager}
                          alt={p.name}
                          className="w-16 h-16 sm:w-24 sm:h-24 object-contain"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm sm:text-base text-gray-900 mb-1">
                          {p.name}
                        </div>
                        <div className="text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                          {p.label}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-600 leading-relaxed" style={{ wordWrap: 'break-word', overflowWrap: 'anywhere' }}>
                          {p.explanation}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  // Fallback if personalities haven't loaded yet - in order: corporate_neutral, friendly, data_driven, aggressive
                  <>
                    <div
                      onClick={() => setPersonality('friendly')}
                      className={cn(
                        "flex items-center gap-6 p-4 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md",
                        personality === 'corporate_neutral'
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 bg-white hover:border-gray-300"
                      )}
                    >
                      <div className="flex-shrink-0">
                        <img
                          src={hiringManager}
                          alt="Michael Rodriguez"
                          className="w-24 h-24 object-contain"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 mb-1">
                          Michael Rodriguez
                        </div>
                        <div className="text-sm font-medium text-gray-700 mb-2">
                          Corporate Neutral
                        </div>
                        <div className="text-sm text-gray-600">
                          Michael is professional, measured, and business-focused. He's direct but respectful, sticks to facts and company policies, and maintains a formal but not cold tone.
                        </div>
                      </div>
                    </div>
                    <div
                      onClick={() => setPersonality('friendly')}
                      className={cn(
                        "flex items-center gap-6 p-4 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md",
                        personality === 'friendly'
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 bg-white hover:border-gray-300"
                      )}
                    >
                      <div className="flex-shrink-0">
                        <img
                          src={hiringManager}
                          alt="Sarah Chen"
                          className="w-24 h-24 object-contain"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 mb-1">
                          Sarah Chen
                        </div>
                        <div className="text-sm font-medium text-gray-700 mb-2">
                          Friendly
                        </div>
                        <div className="text-sm text-gray-600">
                          Sarah is warm, collaborative, and personable. She's open to discussion and flexible, approaching negotiations as a partnership while maintaining professional boundaries.
                        </div>
                      </div>
                    </div>
                    <div
                      onClick={() => setPersonality('data_driven')}
                      className={cn(
                        "flex items-center gap-6 p-4 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md",
                        personality === 'data_driven'
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 bg-white hover:border-gray-300"
                      )}
                    >
                      <div className="flex-shrink-0">
                        <img
                          src={hiringManager}
                          alt="Jennifer Park"
                          className="w-24 h-24 object-contain"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 mb-1">
                          Jennifer Park
                        </div>
                        <div className="text-sm font-medium text-gray-700 mb-2">
                          Data-Driven
                        </div>
                        <div className="text-sm text-gray-600">
                          Jennifer is analytical, structured, and transparent. She references compensation bands, leveling guidelines, and market benchmarks, explaining how offers fit into the broader structure and adjusting only when justified by data.
                        </div>
                      </div>
                    </div>
                    <div
                      onClick={() => setPersonality('aggressive')}
                      className={cn(
                        "flex items-center gap-6 p-4 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md",
                        personality === 'aggressive'
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 bg-white hover:border-gray-300"
                      )}
                    >
                      <div className="flex-shrink-0">
                        <img
                          src={hiringManager}
                          alt="David Thompson"
                          className="w-24 h-24 object-contain"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 mb-1">
                          David Thompson
                        </div>
                        <div className="text-sm font-medium text-gray-700 mb-2">
                          Aggressive Negotiator
                        </div>
                        <div className="text-sm text-gray-600">
                          David is firm, assertive, and sometimes pushy. He uses pressure tactics, emphasizes constraints, and may be less flexible. He's still professional but more direct about limitations.
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-center">
            <Button onClick={handleSetupSubmit} disabled={loading} size="lg" className="text-lg px-12 py-6">
              Start
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!negotiation) {
    return (
      <DashboardLayout>
        <div className="p-8 text-center text-red-500">Negotiation not found.</div>
      </DashboardLayout>
    );
  }

  const isComplete = negotiation.status === 'COMPLETE';
  const isGrading = negotiation.status === 'GRADING';
  const isTerminated = negotiation.status === 'TERMINATED';

  // Grading wait screen
  if (isGrading) {
    return (
      <DashboardLayout>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-4xl">
          <div className="flex items-center gap-4 mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate('/salary-negotiations')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
          </div>

          <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 animate-fade-in">
            <div className="bg-white dark:bg-card rounded-2xl shadow-xl p-8 sm:p-12 max-w-2xl w-full flex flex-col items-center">
              <h1 className="flex items-center gap-2 text-xl text-primary font-semibold mb-2 text-center">
                We're analyzing your negotiation
              </h1>
              <img src={logo} alt="HiredCoach Logo" className="w-20 sm:w-28 mb-4" />
              <div className="flex items-center gap-2 text-lg sm:text-xl text-primary font-semibold mb-2 text-center">
                This might take a minute...
              </div>
              <div className="flex flex-col items-center">
                <svg className="animate-spin w-12 h-12 text-primary mb-2" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
                <span className="text-sm text-muted-foreground">Grading in progress...</span>
              </div>
              <div className="text-center text-muted-foreground mb-2 text-base space-y-2 mt-6">
                We're analyzing your negotiation against best practices; we're not just giving you a grade; we're also going to give you:
              </div>
              <ul className="text-left text-muted-foreground mb-4 text-sm sm:text-base list-disc list-inside space-y-2 mt-6">
                <li>Insight into what the negotiator was thinking</li>
                <li>Direct actionable feedback for your next negotiation</li>
                <li>A detailed breakdown of your strategy, clarity, and relationship management</li>
              </ul>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 max-w-6xl">
        {/* Back Button */}
        <div className="mb-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/salary-negotiations')}
            className="flex items-center gap-2"
            size="sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
        </div>

        {/* Feedback Section - Only show if complete */}
        {isComplete && (
          <>
            {/* Header with Colored Background Based on Grade */}
            <Card className="mb-4 overflow-hidden">
              <CardHeader className={`${getHeaderGradeBg(negotiation.overall_score)} border-b border-gray-200 py-3`}>
                <div className="flex flex-col sm:flex-row justify-between items-center sm:items-center gap-3">
                  <div className="flex items-center gap-2 text-center sm:text-left">
                    <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-gray-700" />
                    <div>
                      <CardTitle className="text-base sm:text-xl text-gray-800">
                        Analysis
                      </CardTitle>
                      <div className="text-xs text-gray-600">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                          <span>{formatDate(negotiation.created_at)}</span>
                          <span className="hidden sm:inline">•</span>
                          <span>{getScenarioDisplayName(negotiation.scenario_type)}</span>
                          {negotiation.scenario_config?.personality && (
                            <>
                              <span className="hidden sm:inline">•</span>
                              <Badge variant="outline" className="w-fit text-xs">
                                {(() => {
                                  const personalityId = negotiation.scenario_config.personality;
                                  const personality = personalities.find(p => p.id === personalityId);
                                  return personality ? personality.name : personalityId.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
                                })()}
                              </Badge>
                            </>
                          )}
                          {negotiation.outcome && (
                            <>
                              <span className="hidden sm:inline">•</span>
                              <Badge variant="outline" className="w-fit text-xs">
                                {negotiation.outcome.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}
                              </Badge>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Large Grade Letter */}
                  <div className="flex flex-col items-center justify-center">
                    <span className={`text-3xl sm:text-5xl font-bold drop-shadow-sm ${getGradeTextColor(negotiation.overall_score)}`}>
                      {getScoreLetter(negotiation.overall_score)}
                    </span>
                    <span className="text-xs mt-0.5 text-gray-600 font-medium uppercase tracking-wider">Grade</span>
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Overall Assessment Card */}
            <Card className="mb-4">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50 border-b py-3">
                <CardTitle className="flex items-center gap-2 text-blue-900 text-base">
                  <MessageCircle className="w-4 h-4" />
                  Overall Assessment
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4 pb-4">
                <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                  {negotiation.summary}
                </p>
              </CardContent>
            </Card>

            {/* Strengths and Areas for Improvement Side by Side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
              {/* Strengths */}
              <Card>
                <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 border-b py-3">
                  <CardTitle className="flex items-center gap-2 text-green-900 text-base">
                    <TrendingUp className="w-4 h-4" />
                    What You Did Well
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-3 pb-3">
                  {negotiation.strengths && negotiation.strengths.length > 0 ? (
                    <ul className="space-y-2">
                      {negotiation.strengths.map((strength, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700 text-sm leading-relaxed">{strength}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-600 text-sm">No specific strengths identified.</p>
                  )}
                </CardContent>
              </Card>

              {/* Areas for Improvement */}
              <Card>
                <CardHeader className="bg-gradient-to-r from-yellow-50 to-amber-50 border-b py-3">
                  <CardTitle className="flex items-center gap-2 text-yellow-900 text-base">
                    <Target className="w-4 h-4" />
                    Areas for Improvement
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-3 pb-3">
                  {negotiation.areas_for_improvement && negotiation.areas_for_improvement.length > 0 ? (
                    <ul className="space-y-2">
                      {negotiation.areas_for_improvement.map((area, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <Target className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700 text-sm leading-relaxed">{area}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-600 text-sm">No specific areas for improvement identified.</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Negotiator's Thoughts Card */}
            {negotiation.thoughts && (
              <Card className="mb-4">
                <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50 border-b py-3">
                  <CardTitle className="flex items-center gap-2 text-amber-900 text-base">
                    <Clock className="w-4 h-4" />
                    Negotiator's Thoughts
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-3 pb-3">
                  <div className="flex flex-col sm:flex-row items-start space-y-3 sm:space-y-0 sm:space-x-4">
                    <div className="flex-shrink-0 w-24 sm:w-28 mx-auto sm:mx-0">
                      <img
                        src={negotiation?.scenario_config?.personality 
                          ? personalityImageMap[negotiation.scenario_config.personality] || hiringManager
                          : hiringManager}
                        alt="Negotiator"
                        className="w-24 sm:w-28 object-contain"
                      />
                    </div>
                    <div className="flex-1 w-full">
                      <div className="bg-white rounded-lg p-3 shadow-sm border border-amber-200">
                        <p className="text-gray-700 leading-relaxed italic text-sm">
                          "{negotiation.thoughts}"
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Coaching Tips Card */}
            {negotiation.coaching_tips && negotiation.coaching_tips.length > 0 && (
              <Card className="mb-4">
                <CardHeader className="bg-gradient-to-r from-purple-50 to-indigo-50 border-b py-3">
                  <CardTitle className="flex items-center gap-2 text-purple-900 text-base">
                    <Lightbulb className="w-4 h-4" />
                    Coaching Tips
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-3 pb-3">
                  <div className="flex flex-col sm:flex-row items-start space-y-3 sm:space-y-0 sm:space-x-4">
                    <div className="flex-shrink-0 w-14 sm:w-16 mx-auto sm:mx-0">
                      <img
                        src={logo}
                        alt="AI Coach"
                        className="w-14 sm:w-16 object-contain"
                      />
                    </div>
                    <div className="flex-1 w-full">
                      <ul className="space-y-2">
                        {negotiation.coaching_tips.map((tip, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <Lightbulb className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700 text-sm leading-relaxed">{tip}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Score Breakdown Card */}
            <Card className="mb-4">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b py-3">
                <CardTitle className="flex items-center gap-2 text-blue-900 text-base">
                  <CheckCircle className="w-4 h-4" />
                  Score Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4 pb-4">
                <div className="flex flex-col lg:flex-row gap-4 items-center lg:items-start">
                  {/* Overall Grade - left side */}
                  <div className={`flex flex-col items-center justify-center rounded-xl py-6 px-6 ${getOverallGradeBg(negotiation.overall_score)} shadow-lg flex-shrink-0`}>
                    <span className="text-5xl sm:text-6xl font-extrabold drop-shadow-sm">
                      {getScoreLetter(negotiation.overall_score)}
                    </span>
                    <span className="text-xs mt-1.5 font-medium uppercase tracking-wider">Overall Grade</span>
                  </div>

                  {/* Individual Scores - right side */}
                  <div className="flex-1 space-y-2 w-full">
                    <div className="group relative rounded-lg p-3 bg-blue-100 hover:bg-blue-200 transition-colors">
                      <div className="hidden sm:flex items-center gap-3">
                        <span className="inline-block font-bold text-4xl text-blue-800 w-14 text-center flex-shrink-0">
                          {getScoreLetter(negotiation.strategy_score)}
                        </span>
                        <div className="flex-1">
                          <div className="text-blue-900 font-semibold text-base mb-0.5">Strategy</div>
                          <div className="text-blue-700 text-xs leading-relaxed">
                            Quality of negotiation strategy including anchoring, tradeoffs, and sequencing.
                          </div>
                        </div>
                      </div>
                      <div className="sm:hidden">
                        <div className="flex items-center gap-3">
                          <span className="inline-block font-bold text-2xl text-blue-800 w-10 text-center">
                            {getScoreLetter(negotiation.strategy_score)}
                          </span>
                          <span className="text-blue-900 font-semibold flex-1 text-base">Strategy</span>
                        </div>
                      </div>
                    </div>
                    <div className="group relative rounded-lg p-3 bg-green-100 hover:bg-green-200 transition-colors">
                      <div className="hidden sm:flex items-center gap-3">
                        <span className="inline-block font-bold text-4xl text-green-800 w-14 text-center flex-shrink-0">
                          {getScoreLetter(negotiation.clarity_score)}
                        </span>
                        <div className="flex-1">
                          <div className="text-green-900 font-semibold text-base mb-0.5">Clarity</div>
                          <div className="text-green-700 text-xs leading-relaxed">
                            How clearly you communicated your value, constraints, and asks.
                          </div>
                        </div>
                      </div>
                      <div className="sm:hidden">
                        <div className="flex items-center gap-3">
                          <span className="inline-block font-bold text-2xl text-green-800 w-10 text-center">
                            {getScoreLetter(negotiation.clarity_score)}
                          </span>
                          <span className="text-green-900 font-semibold flex-1 text-base">Clarity</span>
                        </div>
                      </div>
                    </div>
                    <div className="group relative rounded-lg p-3 bg-yellow-100 hover:bg-yellow-200 transition-colors">
                      <div className="hidden sm:flex items-center gap-3">
                        <span className="inline-block font-bold text-4xl text-yellow-800 w-14 text-center flex-shrink-0">
                          {getScoreLetter(negotiation.relationship_score)}
                        </span>
                        <div className="flex-1">
                          <div className="text-yellow-900 font-semibold text-base mb-0.5">Relationship</div>
                          <div className="text-yellow-700 text-xs leading-relaxed">
                            How well you managed tone, rapport, and long-term relationship considerations.
                          </div>
                        </div>
                      </div>
                      <div className="sm:hidden">
                        <div className="flex items-center gap-3">
                          <span className="inline-block font-bold text-2xl text-yellow-800 w-10 text-center">
                            {getScoreLetter(negotiation.relationship_score)}
                          </span>
                          <span className="text-yellow-900 font-semibold flex-1 text-base">Relationship</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Conversation Section */}
        <div className="-mx-4 sm:mx-0">
          <div>
            <div className="px-0 sm:px-3 py-3 space-y-4 sm:space-y-3 mb-3">
              {negotiation.messages.map((m, idx) => {
                const isUserMessage = m.role === 'user';
                const messageAnalysis = isComplete && negotiation.scenario_config?.message_analysis
                  ? negotiation.scenario_config.message_analysis.find(
                      (analysis: any) => {
                        // Find user message index (count only user messages before this one)
                        const userMessageIndex = negotiation.messages
                          .slice(0, idx)
                          .filter(msg => msg.role === 'user').length;
                        return analysis.message_index === userMessageIndex;
                      }
                    )
                  : null;
                const isExpanded = expandedMessages.has(idx);

                return (
                  <div key={idx} className="space-y-1.5">
                    <div
                      className={`flex flex-col sm:flex-row items-start gap-2 ${isUserMessage ? 'sm:items-end sm:flex-row-reverse' : 'sm:items-start'}`}
                    >
                      {isUserMessage && (
                        <div className="flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 mx-auto sm:mx-0 mb-1 sm:mb-0 sm:mt-1">
                          <UserAvatar user={user} className="w-16 h-16 sm:w-20 sm:h-20 border-0" />
                        </div>
                      )}
                      {!isUserMessage && (
                        <div className="flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 mx-auto sm:mx-0 mb-1 sm:mb-0 sm:mt-1">
                          <img
                            src={negotiation?.scenario_config?.personality 
                              ? personalityImageMap[negotiation.scenario_config.personality] || hiringManager
                              : hiringManager}
                            alt="Hiring Manager"
                            className="w-16 h-16 sm:w-20 sm:h-20 object-contain"
                          />
                        </div>
                      )}
                      <div className={`flex flex-col sm:flex-row items-start gap-2 w-full sm:max-w-[80%] ${isUserMessage ? 'sm:items-end' : ''}`}>
                        <div
                          className={`flex-1 rounded-none sm:rounded-lg px-4 py-3 sm:px-3 sm:py-2 text-base sm:text-sm w-full ${
                            isUserMessage
                              ? 'bg-blue-600 text-white'
                              : 'bg-white border-0 sm:border border-gray-200 text-gray-800 shadow-none sm:shadow-sm'
                          }`}
                        >
                          <div className="text-xs opacity-70 mb-1 sm:mb-0.5 font-medium">
                            {isUserMessage ? 'You' : 'Hiring Manager'}
                          </div>
                          <div className="whitespace-pre-wrap leading-relaxed text-base sm:text-sm">{m.content}</div>
                        </div>
                        {!isUserMessage && m.audio_url && (
                          <AudioPlayerButton 
                            src={m.audio_url.startsWith('http') ? m.audio_url : `${window.location.origin}/api/storage/artifacts/${m.audio_url}`} 
                            className="flex-shrink-0 mt-1 self-center sm:self-start" 
                          />
                        )}
                      </div>
                    </div>
                    
                    {/* Message Analysis - Only for user messages when complete and not skipped */}
                    {isComplete && isUserMessage && messageAnalysis && !messageAnalysis.skip && (
                      <div className="w-full">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const newExpanded = new Set(expandedMessages);
                            if (isExpanded) {
                              newExpanded.delete(idx);
                            } else {
                              newExpanded.add(idx);
                            }
                            setExpandedMessages(newExpanded);
                          }}
                          className="w-full flex items-center justify-between text-xs"
                        >
                          <span>View Analysis</span>
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </Button>
                        
                        {isExpanded && (
                          <Card className="mt-1.5 border-blue-200 w-full">
                            <CardHeader className="py-2 pb-2">
                              <CardTitle className="text-sm">Response Analysis</CardTitle>
                            </CardHeader>
                            <CardContent className="p-3 space-y-2.5">
                              <>
                                  {messageAnalysis.what_they_did_right && (
                                    <div className="bg-green-50 rounded-lg p-2.5 border-l-4 border-green-500">
                                      <div className="flex items-center gap-1.5 mb-1.5">
                                        <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                                        <span className="text-xs font-semibold text-green-900">What You Did Right</span>
                                      </div>
                                      <p className="text-xs text-gray-700 leading-relaxed">{messageAnalysis.what_they_did_right}</p>
                                    </div>
                                  )}
                                  
                                  {messageAnalysis.what_they_did_wrong && (
                                    <div className="bg-red-50 rounded-lg p-2.5 border-l-4 border-red-500">
                                      <div className="flex items-center gap-1.5 mb-1.5">
                                        <Target className="w-3.5 h-3.5 text-red-600" />
                                        <span className="text-xs font-semibold text-red-900">What You Did Wrong</span>
                                      </div>
                                      <p className="text-xs text-gray-700 leading-relaxed">{messageAnalysis.what_they_did_wrong}</p>
                                    </div>
                                  )}
                                  
                                  {messageAnalysis.hiring_manager_thoughts && (
                                    <div className="bg-amber-50 rounded-lg p-2.5 border-l-4 border-amber-500">
                                      <div className="flex items-start gap-2">
                                        <div className="flex-shrink-0 w-10">
                                          <img
                                            src={negotiation?.scenario_config?.personality 
                                              ? personalityImageMap[negotiation.scenario_config.personality] || hiringManager
                                              : hiringManager}
                                            alt="Negotiator"
                                            className="w-10 object-contain"
                                          />
                                        </div>
                                        <div className="flex-1">
                                          <div className="flex items-center gap-1.5 mb-1.5">
                                            <Clock className="w-3.5 h-3.5 text-amber-600" />
                                            <span className="text-xs font-semibold text-amber-900">Negotiator's Thoughts</span>
                                          </div>
                                          <p className="text-xs text-gray-700 italic leading-relaxed">"{messageAnalysis.hiring_manager_thoughts}"</p>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                              </>
                            </CardContent>
                          </Card>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {negotiation.messages.length === 0 && (
                <div className="text-sm text-gray-500 text-center mt-8">
                  Start the negotiation by sending your first message.
                </div>
              )}
              {/* Invisible element at the end for auto-scrolling */}
              <div ref={conversationEndRef} />
            </div>

            {!isComplete && !isGrading && !isTerminated && (
              <div className="space-y-3 px-0 sm:px-3">
                <div className="flex gap-3 items-start px-4 sm:px-0">
                  <Textarea
                    placeholder="Type your next message..."
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    rows={2}
                    className="text-sm flex-1"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                        handleSend();
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleMicClick}
                    disabled={isTranscribing || !isMicrophoneSupported()}
                    className={`w-14 h-14 rounded-full flex items-center justify-center transition focus:outline-none flex-shrink-0 self-start mt-0 shadow-md
                      ${!isMicrophoneSupported() ? 'bg-gray-200 border border-gray-300 cursor-not-allowed' :
                        isRecording ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-white border-2 border-primary hover:bg-primary/5'}
                      ${isTranscribing ? 'opacity-60 cursor-wait' : ''}`}
                    title={!isMicrophoneSupported() ? 'Microphone not supported' :
                           isRecording ? 'Stop recording' : 'Use microphone for speech to text'}
                  >
                    {!isMicrophoneSupported() ? (
                      <Mic className="w-6 h-6 text-gray-400" />
                    ) : isRecording ? (
                      <StopCircle className="w-6 h-6 text-white" />
                    ) : isTranscribing ? (
                      <svg className="animate-spin w-6 h-6 text-primary" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12" cy="12" r="10"
                          stroke="currentColor" strokeWidth="4" fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                        />
                      </svg>
                    ) : (
                      <Mic className="w-6 h-6 text-primary" />
                    )}
                  </button>
                </div>
                {isRecording && (
                  <div className="text-xs text-muted-foreground text-center">
                    Recording: {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
                  </div>
                )}
                {isTranscribing && (
                  <div className="text-xs text-muted-foreground text-center">
                    Transcribing audio...
                  </div>
                )}
                {isTerminated ? (
                  <div className="w-full text-center py-4 px-4 sm:px-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800 font-medium">
                      {negotiation.outcome === 'AGREEMENT_REACHED' 
                        ? '✓ Agreement reached! Preparing your feedback...'
                        : negotiation.outcome === 'EMPLOYER_HELD_FIRM'
                        ? 'The hiring manager has made their final offer. Preparing your feedback...'
                        : 'The negotiation has ended. Preparing your feedback...'}
                    </p>
                    <p className="text-xs text-amber-600 mt-1">Transitioning to grading in a moment...</p>
                  </div>
                ) : (
                  <div className="flex justify-between items-center gap-2 px-4 sm:px-0">
                    {/* Show finish button only after 2+ user messages */}
                    {negotiation && negotiation.messages.filter(m => m.role === 'user').length >= 2 && (
                      <Button 
                        onClick={handleFinish} 
                        disabled={finishing || isRecording}
                        className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 px-6 py-2 rounded-full font-medium"
                      >
                        {finishing ? 'Getting feedback...' : 'Get Feedback ✨'}
                      </Button>
                    )}
                    <div className="flex-1" />
                    <Button onClick={handleSend} disabled={sending || !input.trim() || isRecording} size="sm">
                      {sending ? 'Sending...' : 'Send'}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Premium lock modal */}
      <PremiumLockModal
        open={showPremiumModal}
        onOpenChange={(open) => {
          if (!open) setRateLimitInfo(null);
          setShowPremiumModal(open);
        }}
        featureLabel="Salary Negotiation"
        description={
          rateLimitInfo
            ? `You've used your weekly salary negotiation (${rateLimitInfo.used}/${rateLimitInfo.limit}). ${
                rateLimitInfo.windowEnd
                  ? `Your limit resets on ${new Date(rateLimitInfo.windowEnd).toLocaleDateString()}. `
                  : ''
              }Upgrade to Premium for unlimited practice sessions.`
            : "Salary negotiation practice is available once per week for free users. Upgrade to Premium for unlimited practice sessions."
        }
        ctaLabel="Subscribe now"
      />
    </DashboardLayout>
  );
}
